import { friendlyError } from "@/lib/friendly-errors";
import { createFileRoute, Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Filter, MapPin, ShieldCheck, SlidersHorizontal, X } from "lucide-react";
import { z } from "zod";
import { toast } from "sonner";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";

import { Button } from "@/components/ui/button";
import { TherapistCard } from "@/components/therapists/TherapistCard";
import { TherapistFilterDrawer } from "@/components/therapists/TherapistFilterDrawer";
import { useMyLocation } from "@/components/therapists/LocationPicker";
import { setMyLocation } from "@/lib/user-location.functions";
import {
  ALL_LANGUAGES,
  ALL_MODALITIES,
  ALL_SPECIALTIES,
  THERAPISTS,
  type Therapist,
} from "@/lib/therapists-data";
import {
  COUNTRY_OPTIONS,
  US_STATE_OPTIONS,
  deriveLicensingFromTherapist,
  isTherapistVisibleToUser,
  normaliseCountry,
} from "@/lib/therapist-licensing";
import {
  listPublishedTherapists,
  mapPublishedRowToTherapist,
} from "@/lib/therapists-directory.functions";
import { supabase } from "@/integrations/supabase/client";
import { useIsSignedIn } from "@/hooks/useAuthUser";

const therapistsSearchSchema = z.object({
  q: z.string().optional(),
  countries: z.array(z.string()).optional(),
  languages: z.array(z.string()).optional(),
  approaches: z.array(z.string()).optional(),
  specialisms: z.array(z.string()).optional(),
  formats: z.array(z.string()).optional(),
});

export const Route = createFileRoute("/therapists")({
  validateSearch: therapistsSearchSchema,
  head: () => ({
    meta: [
      { title: "Find a Therapist — Recovery Trauma™" },
      {
        name: "description",
        content:
          "Browse a premium directory of trauma-informed therapists filtered by country, language, therapy approach, and specialism.",
      },
      { property: "og:title", content: "Find a Therapist — Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "Browse a premium directory of trauma-informed therapists filtered by country, language, therapy approach, and specialism.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/therapists" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/therapists" }],
  }),
  component: TherapistsPage,
});

function toggleList(list: string[], value: string) {
  return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
}

function ActiveChip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <button
      type="button"
      onClick={onRemove}
      className="inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border border-gold/35 bg-card px-3 py-1.5 text-xs font-medium text-foreground shadow-sm"
    >
      {label}
      <X className="h-3 w-3 text-muted-foreground" />
    </button>
  );
}

function CountryEditModal({
  open,
  initial,
  onClose,
  onSave,
}: {
  open: boolean;
  initial: { country: string; region_state: string };
  onClose: () => void;
  onSave: (loc: { country: string; region_state: string }) => void;
}) {
  const [country, setCountry] = useState(normaliseCountry(initial.country) || "");
  const [state, setState] = useState(initial.region_state || "");

  useEffect(() => {
    setCountry(normaliseCountry(initial.country) || "");
    setState(initial.region_state || "");
  }, [initial.country, initial.region_state, open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/35 p-4 backdrop-blur-sm md:items-center"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-[1.5rem] border border-border/70 bg-card p-6 shadow-[var(--shadow-soft-lg)]"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            Your location
          </p>
          <h2 className="font-display text-2xl text-foreground">Where are you based?</h2>
          <p className="text-sm leading-6 text-muted-foreground">
            We only surface therapists who are legally permitted to practise in your country.
          </p>
        </div>

        <div className="mt-6 space-y-4">
          <label className="block text-sm font-medium text-foreground">
            Country
            <select
              value={country}
              onChange={(event) => setCountry(event.target.value)}
              className="mt-2 h-12 w-full rounded-2xl border border-border bg-background px-4 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/30"
            >
              <option value="">Browse worldwide</option>
              {COUNTRY_OPTIONS.map((option) => (
                <option key={option.code} value={option.code}>
                  {option.name}
                </option>
              ))}
            </select>
          </label>

          {country === "US" && (
            <label className="block text-sm font-medium text-foreground">
              State
              <select
                value={state}
                onChange={(event) => setState(event.target.value)}
                className="mt-2 h-12 w-full rounded-2xl border border-border bg-background px-4 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/30"
              >
                <option value="">Select your state</option>
                {US_STATE_OPTIONS.map((option) => (
                  <option key={option.code} value={option.code}>
                    {option.name}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>

        <div className="mt-6 flex flex-wrap justify-end gap-3">
          <Button type="button" variant="outline" className="rounded-full" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="goldSoft"
            className="rounded-full px-5"
            onClick={() => {
              onSave({ country, region_state: country === "US" ? state : "" });
              onClose();
            }}
          >
            Save location
          </Button>
        </div>
      </div>
    </div>
  );
}

function TherapistsPage() {
  const search = useSearch({ from: "/therapists" });
  const navigate = useNavigate({ from: "/therapists" });

  const [query, setQuery] = useState(search.q ?? "");
  const [selectedCountries, setSelectedCountries] = useState<string[]>(search.countries ?? []);
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>(search.languages ?? []);
  const [selectedApproaches, setSelectedApproaches] = useState<string[]>(search.approaches ?? []);
  const [selectedSpecialisms, setSelectedSpecialisms] = useState<string[]>(search.specialisms ?? []);
  const [selectedFormats, setSelectedFormats] = useState<string[]>(search.formats ?? []);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const signedIn = useIsSignedIn();

  useEffect(() => {
    navigate({
      search: () => ({
        ...(query.trim() ? { q: query.trim() } : {}),
        ...(selectedCountries.length ? { countries: selectedCountries } : {}),
        ...(selectedLanguages.length ? { languages: selectedLanguages } : {}),
        ...(selectedApproaches.length ? { approaches: selectedApproaches } : {}),
        ...(selectedSpecialisms.length ? { specialisms: selectedSpecialisms } : {}),
        ...(selectedFormats.length ? { formats: selectedFormats } : {}),
      }),
      replace: true,
      resetScroll: false,
    });
  }, [
    navigate,
    query,
    selectedApproaches,
    selectedCountries,
    selectedFormats,
    selectedLanguages,
    selectedSpecialisms,
  ]);

  const { location: userLocation, setLocal: setLocalLocation, loading } = useMyLocation(signedIn);
  const setFn = useServerFn(setMyLocation);
  const queryClient = useQueryClient();

  async function saveLocation(loc: { country: string; region_state: string }) {
    if (signedIn) {
      try {
        await setFn({ data: loc });
        await queryClient.invalidateQueries({ queryKey: ["my-location"] });
      } catch (error) {
        toast.error(friendlyError(error));
        return;
      }
    }

    setLocalLocation(loc);
    toast.success(loc.country ? "Location updated" : "Showing worldwide therapists");
  }

  const locationLabel =
    COUNTRY_OPTIONS.find((option) => option.code === normaliseCountry(userLocation.country))?.name ??
    userLocation.country;

  const listPublishedFn = useServerFn(listPublishedTherapists);
  const { data: publishedRows } = useQuery({
    queryKey: ["published-therapists"],
    queryFn: () => listPublishedFn(),
    staleTime: 60_000,
  });

  const allTherapists: Therapist[] = useMemo(() => {
    const dbMapped = (publishedRows ?? []).map(mapPublishedRowToTherapist);
    // Avoid duplicating any entry that also exists in the curated static list.
    const staticNames = new Set(THERAPISTS.map((t) => t.name.toLowerCase().trim()));
    const dedupedDb = dbMapped.filter((t) => !staticNames.has(t.name.toLowerCase().trim()));
    return [...THERAPISTS, ...dedupedDb];
  }, [publishedRows]);

  const countryOptions = useMemo(
    () => Array.from(new Set(allTherapists.map((therapist) => therapist.country).filter(Boolean))).sort(),
    [allTherapists],
  );
  const languageOptions = useMemo(
    () => ALL_LANGUAGES.filter((language) => allTherapists.some((therapist) => therapist.languages.includes(language))),
    [allTherapists],
  );
  const formatOptions = ["Online", "In-person"];

  const jurisdictionFiltered = useMemo(() => {
    if (!userLocation.country) return allTherapists;

    return allTherapists.filter((therapist) =>
      isTherapistVisibleToUser(deriveLicensingFromTherapist(therapist), userLocation),
    );
  }, [userLocation, allTherapists]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();

    return jurisdictionFiltered.filter((therapist) => {
      if (selectedCountries.length && !selectedCountries.includes(therapist.country)) return false;
      if (
        selectedLanguages.length &&
        !selectedLanguages.some((language) => therapist.languages.includes(language))
      ) {
        return false;
      }
      if (
        selectedApproaches.length &&
        !selectedApproaches.every((approach) => therapist.modalities.includes(approach))
      ) {
        return false;
      }
      if (
        selectedSpecialisms.length &&
        !selectedSpecialisms.some(
          (specialism) =>
            therapist.specialties.includes(specialism) || therapist.issues.includes(specialism),
        )
      ) {
        return false;
      }
      if (selectedFormats.length && !selectedFormats.every((format) => therapist.formats.includes(format as never))) {
        return false;
      }

      if (!q) return true;

      const haystack = [
        therapist.name,
        therapist.country,
        therapist.location,
        therapist.credentials,
        therapist.title,
        therapist.bio,
        ...therapist.languages,
        ...therapist.modalities,
        ...therapist.specialties,
        ...therapist.issues,
      ]
        .join(" ")
        .toLowerCase();

      return haystack.includes(q);
    });
  }, [
    jurisdictionFiltered,
    query,
    selectedApproaches,
    selectedCountries,
    selectedFormats,
    selectedLanguages,
    selectedSpecialisms,
  ]);

  const activeFilterCount =
    selectedCountries.length +
    selectedLanguages.length +
    selectedApproaches.length +
    selectedSpecialisms.length +
    selectedFormats.length;

  function clearFilters() {
    setQuery("");
    setSelectedCountries([]);
    setSelectedLanguages([]);
    setSelectedApproaches([]);
    setSelectedSpecialisms([]);
    setSelectedFormats([]);
  }

  return (
    <div className="pb-16">
      <CountryEditModal
        open={locationModalOpen}
        initial={userLocation}
        onClose={() => setLocationModalOpen(false)}
        onSave={saveLocation}
      />

      <TherapistFilterDrawer
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        countryOptions={countryOptions}
        languageOptions={languageOptions}
        approachOptions={ALL_MODALITIES}
        specialismOptions={ALL_SPECIALTIES}
        formatOptions={formatOptions}
        selectedCountries={selectedCountries}
        selectedLanguages={selectedLanguages}
        selectedApproaches={selectedApproaches}
        selectedSpecialisms={selectedSpecialisms}
        selectedFormats={selectedFormats}
        onToggleCountry={(value) => setSelectedCountries((current) => toggleList(current, value))}
        onToggleLanguage={(value) => setSelectedLanguages((current) => toggleList(current, value))}
        onToggleApproach={(value) => setSelectedApproaches((current) => toggleList(current, value))}
        onToggleSpecialism={(value) => setSelectedSpecialisms((current) => toggleList(current, value))}
        onToggleFormat={(value) => setSelectedFormats((current) => toggleList(current, value))}
        onClear={clearFilters}
      />

      <section className="border-b border-border/50 bg-[linear-gradient(180deg,color-mix(in_oklab,var(--gold-light)_45%,white)_0%,color-mix(in_oklab,var(--background)_92%,var(--gold-light)_8%)_100%)]">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
          <div className="max-w-3xl space-y-5">
            <span className="inline-flex items-center gap-2 rounded-full border border-gold/30 bg-card px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--gold-deep)] shadow-sm">
              <ShieldCheck className="h-3.5 w-3.5" /> Recovery Trauma™ Therapist Directory
            </span>
            <div className="space-y-4">
              <h1 className="text-4xl leading-tight text-foreground sm:text-5xl">
                💛 Find a Therapist
              </h1>
              <p className="max-w-2xl text-base leading-8 text-foreground/78 sm:text-lg">
                Browse qualified, compassionate professionals in a calm, premium directory designed to feel warm, trustworthy, and easy to navigate.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="rounded-[1.75rem] border border-gold/20 bg-card/95 p-5 shadow-[var(--shadow-soft)] sm:p-6">
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
            <div className="space-y-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                  Search the directory
                </p>
                <div className="mt-2 flex h-12 items-center rounded-full border border-border/70 bg-background px-4 shadow-sm">
                  <Filter className="me-3 h-4 w-4 text-[var(--gold-deep)]" />
                  <input
                    type="search"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Name, country, language, therapy approach, specialism…"
                    aria-label="Search therapists"
                    className="h-full w-full bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
                  />
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                {[
                  { label: "Online", active: selectedFormats.includes("Online"), value: "Online" },
                  { label: "In-person", active: selectedFormats.includes("In-person"), value: "In-person" },
                ].map((chip) => (
                  <button
                    key={chip.value}
                    type="button"
                    onClick={() => setSelectedFormats((current) => toggleList(current, chip.value))}
                    className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                      chip.active
                        ? "border-gold/50 bg-[var(--gold-light)] text-[var(--gold-deep)]"
                        : "border-border/70 bg-background text-foreground/75 hover:border-gold/30 hover:text-foreground"
                    }`}
                  >
                    {chip.label}
                  </button>
                ))}

                <button
                  type="button"
                  onClick={() => setLocationModalOpen(true)}
                  className="inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border border-border/70 bg-background px-3 py-1.5 text-xs font-medium text-foreground/80 hover:border-gold/30 hover:text-foreground"
                >
                  <MapPin className="h-3.5 w-3.5 text-[var(--gold-deep)]" />
                  {loading ? "Checking location…" : locationLabel ? `${locationLabel}${userLocation.region_state ? ` · ${userLocation.region_state}` : ""}` : "Worldwide"}
                </button>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 lg:justify-end">
              <Button
                type="button"
                variant="outline"
                className="rounded-full"
                onClick={() => setFiltersOpen(true)}
              >
                <SlidersHorizontal className="h-4 w-4" />
                Filters
                {activeFilterCount > 0 && (
                  <span className="rounded-full bg-[var(--gold-deep)] px-2 py-0.5 text-[11px] font-semibold text-background">
                    {activeFilterCount}
                  </span>
                )}
              </Button>

              {activeFilterCount > 0 && (
                <Button type="button" variant="ghost" className="rounded-full" onClick={clearFilters}>
                  <X className="h-4 w-4" /> Clear
                </Button>
              )}
            </div>
          </div>

          {activeFilterCount > 0 && (
            <div className="mt-4 flex flex-wrap gap-2 border-t border-border/60 pt-4">
              {selectedCountries.map((value) => (
                <ActiveChip key={`country-${value}`} label={value} onRemove={() => setSelectedCountries((current) => current.filter((item) => item !== value))} />
              ))}
              {selectedLanguages.map((value) => (
                <ActiveChip key={`language-${value}`} label={value} onRemove={() => setSelectedLanguages((current) => current.filter((item) => item !== value))} />
              ))}
              {selectedApproaches.map((value) => (
                <ActiveChip key={`approach-${value}`} label={value} onRemove={() => setSelectedApproaches((current) => current.filter((item) => item !== value))} />
              ))}
              {selectedSpecialisms.map((value) => (
                <ActiveChip key={`specialism-${value}`} label={value} onRemove={() => setSelectedSpecialisms((current) => current.filter((item) => item !== value))} />
              ))}
              {selectedFormats.map((value) => (
                <ActiveChip key={`format-${value}`} label={value} onRemove={() => setSelectedFormats((current) => current.filter((item) => item !== value))} />
              ))}
            </div>
          )}
        </div>

        <div className="mt-6 flex flex-col gap-3 rounded-[1.5rem] border border-gold/20 bg-[var(--gold-light)]/25 p-5 text-sm text-foreground/82 shadow-[var(--shadow-soft)] md:flex-row md:items-center md:justify-between">
          <div className="flex items-start gap-3">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[var(--gold-deep)]" />
            <p className="leading-7">
              Recovery Trauma™ carries out document verification before a profile can be listed. Therapists are independent practitioners — we do not employ, supervise, clinically assess, recommend, endorse or guarantee them or their services. Document verification does not guarantee a practitioner's competence, suitability, conduct, treatment or outcomes, so please check credentials and registration independently before engaging anyone.
            </p>
          </div>
          <Link to="/therapist-terms" className="text-sm font-medium text-[var(--gold-deep)] hover:underline">
            View therapist terms
          </Link>
        </div>

        <div className="mt-8 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Directory results
            </p>
            <h2 className="mt-1 text-2xl text-foreground">
              {results.length} therapist{results.length === 1 ? "" : "s"}
            </h2>
          </div>
          {userLocation.country && (
            <p className="text-sm text-muted-foreground">
              Showing therapists who can work with clients in <span className="font-medium text-foreground">{locationLabel}</span>.
            </p>
          )}
        </div>

        {results.length === 0 ? (
          <div className="mt-8 rounded-[1.75rem] border border-dashed border-border/70 bg-card p-10 text-center shadow-[var(--shadow-soft)]">
            <h3 className="text-2xl text-foreground">No therapists match these filters</h3>
            <p className="mx-auto mt-3 max-w-xl text-sm leading-7 text-muted-foreground">
              Try removing one or two filters, or switch your location back to worldwide browsing to see a broader set of verified professionals.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Button type="button" variant="goldSoft" className="rounded-full px-5" onClick={clearFilters}>
                Clear filters
              </Button>
              <Button type="button" variant="outline" className="rounded-full" onClick={() => saveLocation({ country: "", region_state: "" })}>
                Browse worldwide
              </Button>
            </div>
          </div>
        ) : (
          <div className="mt-8 grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
            {results.map((therapist) => (
              <TherapistCard key={therapist.id} therapist={therapist} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
